import type { DirectoryPublic } from '@repo/types/directory';
import type { FilePublicWithPermissions } from '@repo/types/files';
import type { GroupPermissions, UserPermissions } from '@repo/types/globals';
import type { Dispatch, SetStateAction } from 'react';

import { useEditDirectory } from '@repo/api/directory/useEditDirectory';
import { useGetDirectoryPermissions } from '@repo/api/directory/useGetDirectoryPermissions';
import { useEditFile } from '@repo/api/file/useEditFile';
import { useGetFilePermissions } from '@repo/api/file/useGetFIlePermissions';
import { useGetGroupsInfinite } from '@repo/api/group/useGetGroupsInfinite';
import { useGetUsersInfinite } from '@repo/api/user/useGetUsersInfinite';
import { useDebounceValue } from '@repo/hooks/useDebounceValue';
import { useInfiniteQueryResult } from '@repo/hooks/useInfiniteQueryResult';
import { getIsFSEntityFile } from '@repo/lib/fs/getIsFSEntityFile';
import { CheckCircle2, ChevronLeft, DotIcon, Plus, User, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import PermissionLiEntity from '@/components/features/files/PermissionLiEntity';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { InfiniteScroll } from '@/components/ui/infinite-scroll';
import { SearchInput } from '@/components/ui/input';
import { List } from '@/components/ui/list';
import { LoadingOverlay, LoadingOverlayLabel, LoadingOverlaySpinner } from '@/components/ui/loading-overlay';
import { LoadingSwap } from '@/components/ui/loading-swap';
import {
  SelectList,
  SelectListElement,
  SelectListElementContent,
  SelectListElementDescription,
  SelectListElementSkeleton,
  SelectListElementTitle,
  SelectListElementTrigger,
  SelectListInfiniteQueryStates,
} from '@/components/ui/select-list';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { usePermissionChanges } from '@/hooks/fs';
import { getAvatar } from '@/utils/getAvatar';

interface ShareFSEntryProps {
  element?: FilePublicWithPermissions | DirectoryPublic;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  callback?: () => void;
  directoryId: string;
}

export default function ShareFSEntry(shareFSEntryProps: ShareFSEntryProps) {
  const { element, open, onOpenChange, callback, directoryId } = shareFSEntryProps;

  const [tabs, setTabs] = useState<string>('main');
  const [searchValue, setSearchValue] = useState<string>('');
  const searchDebouncedValue = useDebounceValue(searchValue, 400);
  const [selectedUsers, setSelectedUsers] = useState<UserPermissions[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<GroupPermissions[]>([]);

  const isMainTab: boolean = tabs === 'main';
  const isFile: boolean = getIsFSEntityFile(element);

  const { mutateAsync: directoriesMutation, isPending: isDirectoryMutationPending } = useEditDirectory(directoryId);

  const { mutateAsync: filesMutation, isPending: isFileMutationPending, reset } = useEditFile();

  const isEditPending = isFile ? isFileMutationPending : isDirectoryMutationPending;

  const { data: fileData, isError: fileIsError, isSuccess: fileIsSuccess, isPending: fileIsPending } = useGetFilePermissions(element?.id, open && isFile);

  const { data: dirData, isError: dirIsError, isSuccess: dirIsSuccess, isPending: dirIsPending } = useGetDirectoryPermissions(element?.id, open && !isFile);

  const permissionPayload = usePermissionChanges({
    selectedUsers,
    selectedGroups,
    defaultUsers: isFile ? fileData?.users : dirData?.users,
    defaultGroups: isFile ? fileData?.groups : dirData?.groups,
  });

  const isGetPending = isFile ? fileIsPending : dirIsPending;

  const onOpenChangeCb = (value: boolean) => {
    if (!value) {
      onClose();
    }
    onOpenChange(value);
  };

  const onClose = () => {
    setSelectedUsers([]);
    setSelectedGroups([]);
    onOpenChange(false);
    setSearchValue('');
    setTabs('main');
    reset();
    callback?.();
  };

  const onSubmit = async () => {
    if (!element) {
      toast.error('Błąd', {
        description: 'Formularz nie zawiera poprawnego pliku lub folderu',
      });
      return;
    }

    try {
      if (isFile) {
        await filesMutation({
          id: element.id,
          permissionChanges: permissionPayload,
        });
      } else {
        await directoriesMutation({
          id: element.id,
          permissionChanges: permissionPayload,
        });
      }
      onClose();
    } catch {
      toast.error('Błąd', {
        description: 'Nie udało się zaktualizować uprawnień',
      });
    }
  };

  useEffect(() => {
    if (fileIsSuccess && open) {
      const uniqueUsers = Array.from(new Map(fileData.users.map((user) => [String(user.id), user])).values());
      setSelectedUsers(uniqueUsers);

      const uniqueGroups = Array.from(new Map(fileData.groups.map((group) => [String(group.id), group])).values());
      setSelectedGroups(uniqueGroups);
    }
  }, [fileIsSuccess, open, fileData]);

  useEffect(() => {
    if (dirIsSuccess && open) {
      const uniqueUsers = Array.from(new Map(dirData.users.map((user) => [String(user.id), user])).values());
      setSelectedUsers(uniqueUsers);

      const uniqueGroups = Array.from(new Map(dirData.groups.map((group) => [String(group.id), group])).values());
      setSelectedGroups(uniqueGroups);
    }
  }, [dirIsSuccess, open, dirData]);

  useEffect(() => {
    if (fileIsError || dirIsError) {
      onClose();
      toast.error('Błąd', {
        description: `Nie udało się pobrać uprawnień do ${isFile ? 'pliku' : 'folderu'}`,
      });
    }
  }, [fileIsError, dirIsError, onClose, isFile]);

  return (
    <Dialog open={open} onOpenChange={onOpenChangeCb}>
      <DialogContent isClosable={isMainTab} className={'min-h-[85vh] sm:max-w-148'}>
        <DialogDescription className={'sr-only'}>Udostępnij plik lub folder</DialogDescription>
        <DialogBody>
          <LoadingOverlay isLoading={isGetPending}>
            <LoadingOverlaySpinner />
            <LoadingOverlayLabel>Trwa ładowanie danych...</LoadingOverlayLabel>
          </LoadingOverlay>

          {isMainTab ? (
            <DialogHeader className={'border-border-primary border-b'}>
              <DialogTitle>Dostęp: {element?.name}</DialogTitle>
            </DialogHeader>
          ) : (
            <DialogHeader className={'border-border-primary gap-3 border-b'}>
              <DialogTitle className={'sr-only'}>Dostęp</DialogTitle>
              <Button variant={'outline'} onClick={() => setTabs('main')}>
                <ChevronLeft />
              </Button>
              <SearchInput setValue={setSearchValue} placeholder={'Szukaj...'} />
            </DialogHeader>
          )}

          <Tabs
            value={tabs}
            onValueChange={(value) => {
              setSearchValue('');
              setTabs(value);
            }}
            className={'flex grow flex-col gap-3 overflow-auto p-4'}
          >
            <TabsContent value={'main'} className={'space-y-6'}>
              <ShareFSEntryUsersList setSelected={setSelectedUsers} setTab={setTabs} selected={selectedUsers} />
              <ShareFSEntryGroupsList setSelected={setSelectedGroups} setTab={setTabs} selected={selectedGroups} />
            </TabsContent>
            <TabsContent value={'usersSelectedList'} className={'flex flex-col data-[state=active]:h-full'}>
              <ShareFSEntryUsersSelectedDataList searchValue={searchDebouncedValue} selected={selectedUsers} open={open} setSelected={setSelectedUsers} />
            </TabsContent>
            <TabsContent value={'groupsSelectedList'} className={'flex flex-col data-[state=active]:h-full'}>
              <ShareFSEntryGroupsSelectedDataList searchValue={searchDebouncedValue} selected={selectedGroups} open={open} setSelected={setSelectedGroups} />
            </TabsContent>
          </Tabs>
        </DialogBody>
        {isMainTab ? (
          <DialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
            <Button onClick={onClose} type={'button'} variant={'tertiary'} className={'w-full md:w-fit'}>
              Anuluj
            </Button>
            <Button disabled={isGetPending} onClick={onSubmit} type={'submit'} variant={'primary'} className={'w-full md:w-fit'}>
              <LoadingSwap isLoading={isEditPending} className="flex items-center gap-2">
                <CheckCircle2 />
                Zapisz
              </LoadingSwap>
            </Button>
          </DialogFooter>
        ) : (
          <DialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
            <Button onClick={() => setTabs('main')} type={'button'} variant={'tertiary'}>
              Zapisz
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface ShareFSEntryListProps<T> {
  selected: T[];
  setSelected: Dispatch<SetStateAction<T[]>>;
  setTab: Dispatch<SetStateAction<string>>;
}

function ShareFSEntryUsersList(shareFSEntryUsersListProps: ShareFSEntryListProps<UserPermissions>) {
  const { selected, setTab, setSelected } = shareFSEntryUsersListProps;

  const uniqueSelected = useMemo<UserPermissions[]>(() => {
    const seen = new Set<string>();
    return selected.filter((user) => {
      const key = String(user.id);
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [selected]);

  const handleRemoveEntity = (id: number) => {
    setSelected((prev) => prev.filter((user) => String(user.id) !== String(id)));
  };

  const handlePermissionChange = (id: number, newValue: string) => {
    setSelected((prev) =>
      prev.map((user) =>
        String(user.id) === String(id)
          ? {
              ...user,
              permissions: {
                read: false,
                write: false,
                edit: false,
                [newValue]: true,
              },
            }
          : user
      )
    );
  };

  return (
    <div className={'flex flex-col gap-4'}>
      <div className={'flex justify-between'}>
        <h5 className={'font-medium'}>Użytkownicy:</h5>
        <Button size="sm" variant="tertiary" onClick={() => setTab('usersSelectedList')}>
          <Plus />
          Dodaj
        </Button>
      </div>

      <List>
        {uniqueSelected.length === 0 && (
          <li className="p-3">
            <p className={'text-muted-foreground my-3 text-center text-sm'}>Dodaj użytkowników</p>
          </li>
        )}
        {uniqueSelected.map((entity) => (
          <PermissionLiEntity
            key={String(entity.id)}
            data={{
              id: entity.id,
              title: entity.name + ' ' + entity.surname,
              avatarURL: entity.avatarURL ?? undefined,
            }}
            permissions={entity.permissions}
            sources={entity.sources}
            onPermissionChange={handlePermissionChange}
            onRemove={() => handleRemoveEntity(entity.id)}
          />
        ))}
      </List>
    </div>
  );
}

function ShareFSEntryGroupsList(shareFSEntryGroupsListProps: ShareFSEntryListProps<GroupPermissions>) {
  const { selected, setTab, setSelected } = shareFSEntryGroupsListProps;

  // Deduplicate groups for rendering
  const uniqueSelected = useMemo<GroupPermissions[]>(() => {
    const seen = new Set<string>();
    return selected.filter((group) => {
      const key = String(group.id);
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [selected]);

  const handleRemoveEntity = (id: number) => {
    setSelected((prev) => prev.filter((group) => String(group.id) !== String(id)));
  };

  const handlePermissionChange = (id: number, newValue: string) => {
    setSelected((prev) =>
      prev.map((group) =>
        String(group.id) === String(id)
          ? {
              ...group,
              permissions: {
                read: false,
                write: false,
                edit: false,
                [newValue]: true,
              },
            }
          : group
      )
    );
  };

  return (
    <div className={'flex flex-col gap-4'}>
      <div className={'flex justify-between'}>
        <h5 className={'font-medium'}>Grupy:</h5>
        <Button size="sm" variant="tertiary" onClick={() => setTab('groupsSelectedList')}>
          <Plus />
          Dodaj
        </Button>
      </div>

      <List>
        {uniqueSelected.length === 0 && (
          <li className="p-3">
            <p className={'text-muted-foreground my-3 text-center text-sm'}>Dodaj grupy</p>
          </li>
        )}
        {uniqueSelected.map((entity) => (
          <PermissionLiEntity
            key={entity.id}
            data={{
              id: entity.id,
              title: entity.name,
              avatarURL: entity.avatarURL ?? undefined,
            }}
            permissions={entity.permissions}
            sources={entity.sources}
            onPermissionChange={handlePermissionChange}
            onRemove={() => handleRemoveEntity(entity.id)}
          />
        ))}
      </List>
    </div>
  );
}

interface ShareFSEntrySelectedDataListProps<SelectedT> {
  searchValue: string;
  selected: SelectedT[];
  open: boolean;
  setSelected: Dispatch<SetStateAction<SelectedT[]>>;
}

function ShareFSEntryUsersSelectedDataList(shareFSEntrySelectedDataListProps: ShareFSEntrySelectedDataListProps<UserPermissions>) {
  const { selected, open, setSelected, searchValue } = shareFSEntrySelectedDataListProps;
  const { errorMessage: usersErrorMessage, ...getUsersInfinite } = useGetUsersInfinite(searchValue, open);

  const { data: users, isEmpty: usersIsEmpty, noResults: usersNoResults } = useInfiniteQueryResult(getUsersInfinite.data, searchValue);

  const isUserInherited = (user: UserPermissions): boolean => {
    return user?.sources?.some((source) => source.isInherited) || false;
  };

  const selectedNonInheritedIds = useMemo<string[]>(() => selected.filter((user) => !isUserInherited(user)).map((u) => String(u.id)), [selected]);

  const onSelect = (ids: string[]) => {
    const selectedIds = new Set(ids);

    setSelected((prevSelected) => {
      const result: UserPermissions[] = [];
      const processedIds = new Set<string>();

      prevSelected.forEach((user) => {
        if (processedIds.has(String(user.id))) {
          return;
        }

        const isInherited = isUserInherited(user);
        const isNowSelected = selectedIds.has(String(user.id));

        if (isInherited && isNowSelected) {
          result.push({
            ...user,
            sources: undefined,
          });
          processedIds.add(String(user.id));
        } else if (isInherited && !isNowSelected) {
          result.push(user);
          processedIds.add(String(user.id));
        } else if (!isInherited && isNowSelected) {
          result.push(user);
          processedIds.add(String(user.id));
        }
      });

      const newUsers = users
        .filter((u) => selectedIds.has(String(u.id)) && !processedIds.has(String(u.id)))
        .map((user): UserPermissions => ({
          ...user,
          permissions: {
            read: true,
            write: false,
            edit: false,
          },
          sources: undefined,
        }));

      const combined = [...result, ...newUsers];
      const uniqueResult = Array.from(new Map(combined.map((user) => [String(user.id), user])).values());
      return uniqueResult;
    });
  };

  return (
    <SelectList type={'multiple'} selected={selectedNonInheritedIds} setSelected={onSelect}>
      <SelectListInfiniteQueryStates
        query={getUsersInfinite}
        isEmpty={usersIsEmpty}
        noResults={usersNoResults}
        isEmptyMessage={'Wyszukaj użytkownika'}
        noResultMessage={'Nie znaleziono użytkownika'}
        errorMessage={usersErrorMessage}
      />

      {users.map((entity) => {
        const selectedUser = selected.find((u) => String(u.id) === String(entity.id));
        const isInherited = selectedUser ? isUserInherited(selectedUser) : false;
        return (
          <SelectListElement key={String(entity.id)} id={String(entity.id)}>
            <Avatar size={'sm'}>
              <AvatarImage src={getAvatar(entity.avatarURL)} alt={entity.name} />
              <AvatarFallback>
                <User />
              </AvatarFallback>
            </Avatar>
            <SelectListElementContent>
              <div className="flex items-center gap-1">
                <SelectListElementTitle>
                  {entity.name} {entity.surname}
                </SelectListElementTitle>
                {isInherited && (
                  <>
                    <DotIcon className="text-muted-foreground size-3.5" />

                    <p className="text-muted-foreground text-xs">Dziedziczone</p>
                  </>
                )}
              </div>
              <SelectListElementDescription>{entity.email}</SelectListElementDescription>
            </SelectListElementContent>

            <SelectListElementTrigger>
              {({ selected, toggleElement }) => <Checkbox checked={selected} onCheckedChange={toggleElement} className="after:absolute after:inset-0" />}
            </SelectListElementTrigger>
          </SelectListElement>
        );
      })}
      <InfiniteScroll hasNextPage={getUsersInfinite.hasNextPage} isLoading={getUsersInfinite.isLoading} isFetching={getUsersInfinite.isFetching} next={getUsersInfinite.fetchNextPage} threshold={0.65}>
        {getUsersInfinite.hasNextPage && <SelectListElementSkeleton />}
      </InfiniteScroll>
    </SelectList>
  );
}

function ShareFSEntryGroupsSelectedDataList(shareFSEntrySelectedDataListProps: ShareFSEntrySelectedDataListProps<GroupPermissions>) {
  const { selected, open, setSelected, searchValue } = shareFSEntrySelectedDataListProps;
  const { errorMessage: groupsErrorMessage, ...getGroupsInfinite } = useGetGroupsInfinite(searchValue, open);

  const { data: groups, isEmpty: groupsIsEmpty, noResults: groupsNoResults } = useInfiniteQueryResult(getGroupsInfinite.data, searchValue);

  const isGroupInherited = (group: GroupPermissions): boolean => {
    return group?.sources?.some((source) => source.isInherited) || false;
  };

  const selectedNonInheritedIds = useMemo<string[]>(() => selected.filter((group) => !isGroupInherited(group)).map((g) => String(g.id)), [selected]);

  const onSelect = (ids: string[]) => {
    const selectedIds = new Set(ids);

    setSelected((prevSelected) => {
      const result: GroupPermissions[] = [];
      const processedIds = new Set<string>();

      prevSelected.forEach((group) => {
        if (processedIds.has(String(group.id))) {
          return;
        }

        const isInherited = isGroupInherited(group);
        const isNowSelected = selectedIds.has(String(group.id));

        if (isInherited && isNowSelected) {
          result.push({
            ...group,
            sources: undefined,
          });
          processedIds.add(String(group.id));
        } else if (isInherited && !isNowSelected) {
          result.push(group);
          processedIds.add(String(group.id));
        } else if (!isInherited && isNowSelected) {
          result.push(group);
          processedIds.add(String(group.id));
        }
      });

      const newGroups = groups
        .filter((g) => selectedIds.has(String(g.id)) && !processedIds.has(String(g.id)))
        .map((group): GroupPermissions => ({
          ...group,
          permissions: {
            read: true,
            write: false,
            edit: false,
          },
          sources: undefined,
        }));

      const combined = [...result, ...newGroups];
      const uniqueResult = Array.from(new Map(combined.map((group) => [String(group.id), group])).values());
      return uniqueResult;
    });
  };

  return (
    <SelectList type={'multiple'} selected={selectedNonInheritedIds} setSelected={onSelect}>
      <SelectListInfiniteQueryStates
        query={getGroupsInfinite}
        isEmpty={groupsIsEmpty}
        noResults={groupsNoResults}
        isEmptyMessage={'Wyszukaj grupy'}
        noResultMessage={'Nie znaleziono grupy'}
        errorMessage={groupsErrorMessage}
      />

      {groups.map((entity) => {
        const selectedGroup = selected.find((g) => String(g.id) === String(entity.id));
        const isInherited = selectedGroup ? isGroupInherited(selectedGroup) : false;
        return (
          <SelectListElement key={String(entity.id)} id={String(entity.id)}>
            <Avatar size={'sm'}>
              <AvatarImage src={getAvatar(entity.avatarURL)} alt={entity.name} />
              <AvatarFallback>
                <Users />
              </AvatarFallback>
            </Avatar>
            <SelectListElementContent>
              <SelectListElementTitle>
                {entity.name}
                {isInherited && (
                  <Badge variant={'muted'} border size={'sm'} className="ml-2">
                    Dziedziczone
                  </Badge>
                )}
              </SelectListElementTitle>
              <SelectListElementDescription>Grupa</SelectListElementDescription>
            </SelectListElementContent>

            <SelectListElementTrigger>
              {({ selected, toggleElement }) => <Checkbox checked={selected} onCheckedChange={toggleElement} className="after:absolute after:inset-0" />}
            </SelectListElementTrigger>
          </SelectListElement>
        );
      })}
      <InfiniteScroll
        hasNextPage={getGroupsInfinite.hasNextPage}
        isLoading={getGroupsInfinite.isLoading}
        isFetching={getGroupsInfinite.isFetching}
        next={getGroupsInfinite.fetchNextPage}
        threshold={0.65}
      >
        {getGroupsInfinite.hasNextPage && <SelectListElementSkeleton />}
      </InfiniteScroll>
    </SelectList>
  );
}
